import { useGetServerTime } from '@engoo/react-server-time';
import useUnauthenticatedRedirect from 'app/components/auth/useUnauthenticatedRedirect';
import { useHistory, useHistoryLocation } from 'app/components/router/History';
import * as Schema from 'app/constants/schemas';
import { useBookingInterface } from 'app/containers/rtc/BookingContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import {
  createBotStore,
  BotStoreProvider,
  setBotState,
  deleteBot,
  resetBotStore,
  designateBotAsType,
  setBotMeta,
} from 'app/containers/rtc/BotContext';
import HasRtcDebug from 'app/containers/rtc/HasRtcDebug';
import { RtcNotifications, useRtcNotifier } from 'app/containers/rtc/Notifier';
import { useIsSpeaking } from 'app/containers/rtc/VolumeAnalyserContext';
import { getAuthUser } from 'app/models/auth/selectors';
import { BookingTypeSettings } from 'app/models/rtc/booking/constants';
import { ParticipantType } from 'app/models/rtc/constants';
import {
  fetchParticipants,
  launchBot,
  uploadChatImage,
} from 'app/models/rtc/remote';
import joinRoom from 'app/models/rtc/remote/joinRoom';
import { notify } from 'app/services/errorReporter';
import { History } from 'app/services/history';
import { debug } from 'app/services/logger';
import {
  Caller,
  RTC_CONNECTING,
  RTC_FAILED,
  RTC_OFF,
  RTC_ON,
  RTC_ROOM_EVENTS,
  SERVER_WATCHDOG_CONFIG,
} from 'app/services/rtc/constants';
import RTCRoom from 'app/services/rtc/Room';
import { trackStateForPeer } from 'app/services/rtc/TrackState';
import Watchdog from 'app/services/rtc/Watchdog';
import bindMethods from 'app/utils/bindMethods';
import { bindEvents, unbindEvents } from 'app/utils/events';
import TaskQueue from 'app/utils/queues/TaskQueue';
import PropTypes from 'prop-types';
import React, { Component, createContext, useContext } from 'react';
import { connect } from 'react-redux';
import { DeviceManagerState, useDeviceManagerState } from './DeviceManagerContext';
import { usePeersControl } from './PeersContext';
import {
  RtcStateProvider,
  useRtcStateStore,
  setCaller,
  getRtcState,
  setSignalingState,
  setSignalingTimeout,
  setSignalingError,
} from 'app/containers/rtc/RtcStateContext';
import { useRoomsCredentials } from 'app/services/rtc/useRoomsCredentials';
import { useConstant } from 'app/utils/react';
import { useRequiredFeatures } from 'app/containers/rtc/BookingContext/useRequiredFeatures';
import { Set } from 'immutable';

const RtcControlContext = createContext();
const { Consumer: GetRtcControl, Provider: SetRtcControl } = RtcControlContext;
export { GetRtcControl, SetRtcControl };

export const useRTC = () => useContext(RtcControlContext);

export const withRTC = Wrap => (props) => (
  <GetRtcControl>{(rtc) => {
    if (rtc === undefined) {
      return null;
    }

    return (<Wrap {...props} rtc={rtc} />);
  }}</GetRtcControl>
);

export const rtcContextPropType = PropTypes.shape({
  close:                       PropTypes.func.isRequired,
  launchBot:                   PropTypes.func.isRequired,
  reconnect:                   PropTypes.func.isRequired,
  resetPeerConnection:         PropTypes.func.isRequired,
  sendBotCommand:              PropTypes.func.isRequired,
  uploadChatImage:             PropTypes.func.isRequired,

  // optional, only when debug mode is enabled and permitted
  debug: PropTypes.shape({
    failPeerConnection: PropTypes.func.isRequired,
  }),
});
class RTCProvider extends Component {
  constructor(props) {
    super(props);

    this.room = props.room || new RTCRoom();
    this.queue = new TaskQueue();
    this.watchdog = new Watchdog(SERVER_WATCHDOG_CONFIG);

    this.rtcContext = {
      close: () => this.close(RTC_OFF),
      reconnect: () => {
        // Reset watchdog's state so the counting starts over
        this.watchdog.stop();
        this.reconnect();
      },
      ...bindMethods(this, [
        'launchBot',
        'uploadChatImage',
      ]),
      ...bindMethods(this.room, [
        'sendBotCommand',
        'resetPeerConnection',
        'getActionStream',
        'dumpAllSdps',
      ]),
      debug: {
        failPeerConnection: (id) => this.room.debug.failPeerConnection(id),
      },
    };

    this.watchdog.bindEvents({
      warning: (timeToReconnect) => setSignalingTimeout(this.props.rtcStore, {
        timeToReconnect,
        triggeredAt: Date.now(),
      }),
      timeout: () => this.reconnect(),
    });

    // Actions out
    this.roomEventMap = {
      [RTC_ROOM_EVENTS.signalingStateChange]: state => {
        const { rtcStore } = this.props;

        setSignalingState(
          rtcStore,
          // When auto reconnecting is on, watchdog will trigger
          // another reconnect after failure so report it as
          // connecting
          state === RTC_FAILED && this.isAutoReconnectEnabled()
          ? RTC_CONNECTING
          : state,
        );

        if (state === RTC_ON || state === RTC_OFF) {
          setSignalingTimeout(rtcStore, undefined);
          this.watchdog.stop();
        }

        if (state === RTC_OFF) {
          this.props.bookingInterface.clearActiveBooking();
        }
      },

      [RTC_ROOM_EVENTS.signalingError]: error => {
        setSignalingError(this.props.rtcStore, error);
      },

      [RTC_ROOM_EVENTS.reconnect]: () => {
        this.reconnect();
      },

      [RTC_ROOM_EVENTS.botStateChange]: (peerId, botState) => {
        setBotState(this.props.botStore, peerId, botState);
      },

      [RTC_ROOM_EVENTS.addPeer]: (id, synthetic) => {
        this.props.peersControl.addPeer({ id });
        if (!synthetic) {
          this.notify(RtcNotifications.Join);
        }
      },

      [RTC_ROOM_EVENTS.removePeer]: (id, synthetic) => {
        deleteBot(this.props.botStore, id);
        this.props.peersControl.removePeer(id);
        if (!synthetic) {
          this.notify(RtcNotifications.Part);
        }
      },

      [RTC_ROOM_EVENTS.peerConnectionStateChange]: (id, state) => {
        this.props.peersControl.setPeerConnectionState(id, state);
      },

      [RTC_ROOM_EVENTS.peerConnectionError]: (id, error) => {
        this.props.peersControl.setPeerConnectionError(id, error);
      },

      [RTC_ROOM_EVENTS.peerConnectionTimeout]: (id, connectionTimeout) => {
        this.props.peersControl.setPeerTimeoutWarning(id, connectionTimeout);
      },

      [RTC_ROOM_EVENTS.peerAudioStreamChange]: (id, stream) => {
        this.props.peersControl.setPeerAudioStream(id, stream);
      },

      [RTC_ROOM_EVENTS.peerVideoStreamChange]: (id, stream) => {
        this.props.peersControl.setPeerVideoStream(id, stream);
      },

      [RTC_ROOM_EVENTS.peerScreenStreamChange]: (id, stream) => {
        this.props.peersControl.setPeerScreenStream(id, stream);
      },

      [RTC_ROOM_EVENTS.peerStreamStatusChange]: (id, { audio, video, speaking }) => {
        this.props.peersControl.setPeerMediaStatus(id, { audio, video, speaking });
      },

      [RTC_ROOM_EVENTS.peerUpgradedToUser]: this.refreshParticipantsList,
    };
  }

  componentDidMount() {
    const { bookingState } = this.props;

    bindEvents(this.room, this.roomEventMap);

    // Join the room if there is one in the booking state.
    if (bookingState && bookingState.rtcRoom) {
      this.joinRoom();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      devices,
      isSpeaking,
      bookingState = {},
      user,
    } = this.props;

    const prevBookingState = prevProps.bookingState || {};

    if (devices && devices !== prevProps.devices) {
      if (devices.video.stream !== prevProps.devices.video.stream) {
        this.room.setLocalVideoStream(devices.video.stream);
      }

      if (devices.audio.stream !== prevProps.devices.audio.stream) {
        this.room.setLocalAudioStream(devices.audio.stream);
      }

      if (devices.screen.stream !== prevProps.devices.screen.stream) {
        this.room.setLocalScreenStream(devices.screen.stream);
      }

      this.room.setMediaStatus({
        audio: trackStateForPeer(devices.audio.trackState),
        video: trackStateForPeer(devices.video.trackState),
      });
    }

    if (isSpeaking !== prevProps.isSpeaking) {
      this.room.setLocalStreamSpeakingState(isSpeaking);
    }

    // If the room changes, close the old room and try connecting to the new room.
    if (prevBookingState.rtcRoom !== bookingState.rtcRoom) {
      if (bookingState.rtcRoom) {
        this.joinRoom();
      } else if (prevBookingState.rtcRoom) {
        this.close(RTC_OFF);
      }
    }

    // If booking state exists and the user changes, reconnect.
    if (!prevProps.user && user && bookingState.rtcRoom) {
      // Try to reuse the secret after logging in, this will
      // preserve the ownership of chat messages for example
      debug('User changed, reconnecting to the room.');
      this.reconnect();
    }

    if (prevBookingState.rtcRoom && prevBookingState.displayName !== bookingState.displayName) {
      if (bookingState.displayName !== undefined) {
        this.room.updateParticipantName(bookingState.displayName);
      }
    }
  }

  componentWillUnmount() {
    this.close();
    unbindEvents(this.room, this.roomEventMap);
  }

  isAutoReconnectEnabled() {
    const { bookingState } = this.props;

    if (bookingState) {
      const { controlsConnection } = BookingTypeSettings[bookingState.bookingType];
      return !controlsConnection;
    } else {
      return false;
    }
  }

  /**
   * Connects with to a room.
   */
  joinRoom() {
    this.queue.abort();
    this.queue.push(async (cancelToken) => {
      const storedSecret = this.fetchPersistedRoomSecret();
      const { rtcRoom, joinParams, displayName } = this.props.bookingState;
      const { requiredFeatures } = this.props;

      try {
        if (this.isAutoReconnectEnabled()) {
          this.watchdog.start();
        }

        const { rtcStore } = this.props;
        setSignalingError(rtcStore, undefined);
        setSignalingTimeout(rtcStore, undefined);
        setSignalingState(rtcStore, RTC_CONNECTING);

        const joinResult = await this.props.joinRoom({
          id:     rtcRoom,
          secret: storedSecret,
          joinParams,
          cancelToken,
        });

        const { upgradedToUser } = this.handlePostJoinSecretPersistence(
          storedSecret,
          joinResult,
        );

        const { token, url, participant, secret, upgradedToHost } = joinResult;

        const caller = new Caller({
          id: participant.id,
          secret,
        });

        setCaller(this.props.rtcStore, caller);
        this.room.connect({
          id: rtcRoom,
          url,
          caller,
          token,
          upgradedToUser: upgradedToUser || upgradedToHost,
          requiredFeatures,
          displayName,
        });

        if (upgradedToUser) {
          this.refreshParticipantsList();
        }
      } catch (error) {
        if (cancelToken.isCancelled()) {
          return;
        }

        if (storedSecret && error.status === 403) {
          const { deleteRoomSecret } = this.props.roomsCredentials;

          deleteRoomSecret(rtcRoom);
          this.joinRoom();
          return;
        }

        try {
          // Attempt to handle the booking error through the booking interface.
          this.props.bookingInterface.handleBookingError(error);
          this.watchdog.stop();
        } catch (err) {
          // Awaiting on close() here would cause deadlock because
          // `joinRoom` and `close` are on the same process queue
          this.close(this.isAutoReconnectEnabled() ? RTC_CONNECTING : RTC_FAILED);

          // Set the signaling error after `close` finished (otherwise
          // `close` will erase the error)
          notify(err);
          setSignalingError(this.props.rtcStore, err);
        }
      }
    });
  }

  fetchPersistedRoomSecret() {
    const { rtcRoom } = this.props.bookingState;
    const { getRoomSecret } = this.props.roomsCredentials;

    return getRoomSecret(rtcRoom);
  }

  handlePostJoinSecretPersistence(oldSecret, joinResult) {
    const { rtcRoom } = this.props.bookingState;
    const {
      setRoomSecret,
      deleteRoomSecret,
    } = this.props.roomsCredentials;

    const { participant, secret: newSecret } = joinResult;

    const upgradedToUser = Boolean(oldSecret && participant.user);
    const noSecretEchoed = Boolean(oldSecret && !newSecret);

    if (upgradedToUser || noSecretEchoed) {
      deleteRoomSecret(rtcRoom);
    } else if (newSecret) {
      setRoomSecret(rtcRoom, newSecret);
    }

    return { upgradedToUser };
  }

  reconnect() {
    // If there is no booking state, fetch the last booking.
    // Otherwise, re-establish the signal.
    if (!this.props.bookingState) {
      this.props.bookingInterface.fetchLastBooking();
    } else {
      this.joinRoom();
    }
  }

  /**
   * @params {RTC_OFF | RTC_FAILED} [reason]
   */
  close(reason = RTC_OFF) {
    this.queue.abort();
    resetBotStore(this.props.botStore);
    // It's important to close the room ASAP because as part
    // of clearing the active booking, url is cleared from the
    // URL. Ideally it'd be the other way around, clearing the URL
    // triggers disconnect. This is WIP in #2406
    this.room.close(reason);

    const { rtcStore } = this.props;
    setSignalingError(rtcStore, undefined);
    setSignalingTimeout(rtcStore, undefined);
  }

  /**
   * @param {RtcBotType} botType
   * @returns {string} - The participant ID of the bot.
   */
  async launchBot(botType) {
    const { participantId, meta } = await this.props.launchBot(this.props.bookingState.rtcRoom, botType);
    const { botStore } = this.props;
    designateBotAsType(botStore, participantId, botType);
    setBotMeta(botStore, participantId, meta);
    return participantId;
  }

  /**
   * @param {Blob} file
   * @returns {Object} - An object containing the image url and an id
   */
  async uploadChatImage(file) {
    const { caller } = getRtcState(this.props.rtcStore);

    return await this.props.uploadChatImage(this.room.id, {
      file,
      ...(caller.secret ? { secret: caller.secret } : {}),
    });
  }

  notify(name) {
    this.props.notifier.notify(name);
  }

  refreshParticipantsList = async () => {
    const { bookingType, bookingId } = this.props.bookingState;

    await this.props.fetchParticipants(bookingType, bookingId);
  };

  render() {
    return (
      <SetRtcControl value={this.rtcContext}>
        {this.props.children}
      </SetRtcControl>
    );
  }
}

RTCProvider.propTypes = {
  children:                    PropTypes.node.isRequired,
  currentBookingId:            PropTypes.string,

  bookingState:                PropTypes.shape({
    booking:                   PropTypes.object,
    bookingId:                 PropTypes.string.isRequired,
    bookingType:               PropTypes.string.isRequired,
    lessonRole:                PropTypes.string,
    participantType:           PropTypes.oneOf(Object.values(ParticipantType)),
    rtcRoom:                   PropTypes.string,
    joinParams:                PropTypes.object,
    displayName:               PropTypes.string,
  }),
  bookingInterface:            PropTypes.shape({
    clearActiveBooking:        PropTypes.func.isRequired,
    clearBookings:             PropTypes.func.isRequired,
    createBooking:             PropTypes.func.isRequired,
    fetchBooking:              PropTypes.func.isRequired,
    fetchLastBooking:          PropTypes.func.isRequired,
    handleBookingError:        PropTypes.func.isRequired,
  }).isRequired,

  botStore:                    PropTypes.object.isRequired,

  rtcStore:                    PropTypes.object.isRequired,

  user:                        PropTypes.instanceOf(Schema.User),

  joinRoom:                 PropTypes.func.isRequired,

  launchBot:                   PropTypes.func.isRequired,

  uploadChatImage:             PropTypes.func.isRequired,

  fetchParticipants:           PropTypes.func.isRequired,

  location:                    PropTypes.object.isRequired,
  history:                     PropTypes.instanceOf(History).isRequired,
  unauthenticatedRedirect:     PropTypes.func.isRequired,

  room:                        PropTypes.instanceOf(RTCRoom),

  devices:                     PropTypes.instanceOf(DeviceManagerState).isRequired,
  isSpeaking:                  PropTypes.bool.isRequired,

  peersControl:                PropTypes.shape({
    addPeer:                   PropTypes.func.isRequired,
    removePeer:                PropTypes.func.isRequired,
    setPeerAudioStream:        PropTypes.func.isRequired,
    setPeerVideoStream:        PropTypes.func.isRequired,
    setPeerScreenStream:       PropTypes.func.isRequired,
    setPeerMediaStatus:        PropTypes.func.isRequired,
    setPeerConnectionState:    PropTypes.func.isRequired,
    setPeerConnectionError:    PropTypes.func.isRequired,
    setPeerTimeoutWarning:     PropTypes.func.isRequired,
  }),

  getServerTime:               PropTypes.func.isRequired,

  notifier: PropTypes.shape({
    notify: PropTypes.func.isRequired,
  }),

  roomsCredentials:            PropTypes.shape({
    getRoomSecret:             PropTypes.func.isRequired,
    setRoomSecret:             PropTypes.func.isRequired,
    deleteRoomSecret:          PropTypes.func.isRequired,
  }),

  requiredFeatures:            PropTypes.instanceOf(Set).isRequired,

  enableDebug:                 PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  user: getAuthUser(state),
});

const mapDispatchToProps = {
  fetchParticipants,
  joinRoom,
  launchBot,
  uploadChatImage,
};

const RTCProviderInjectDependencies = props => {
  const notifier = useRtcNotifier();
  const devices = useDeviceManagerState();
  const bookingInterface = useBookingInterface();
  const bookingState = useBookingState();
  const isSpeaking = useIsSpeaking();
  const peersControl = usePeersControl();
  const history = useHistory();
  const location = useHistoryLocation();
  const getServerTime = useGetServerTime();
  const roomsCredentials = useRoomsCredentials();
  const botStore = useConstant(() => createBotStore());
  const requiredFeatures = useRequiredFeatures();

  const rtcStore = useRtcStateStore();

  const unauthenticatedRedirect = useUnauthenticatedRedirect();

  return (
    <HasRtcDebug>{(enableDebug) => (
      <BotStoreProvider store={botStore}>
        <RTCProvider
          isSpeaking={isSpeaking}
          peersControl={peersControl}
          devices={devices}
          bookingInterface={bookingInterface}
          bookingState={bookingState}
          botStore={botStore}
          rtcStore={rtcStore}
          location={location}
          history={history}
          unauthenticatedRedirect={unauthenticatedRedirect}
          notifier={notifier}
          enableDebug={enableDebug}
          getServerTime={getServerTime}
          roomsCredentials={roomsCredentials}
          requiredFeatures={requiredFeatures}

          {...props}
        />
      </BotStoreProvider>
    )}</HasRtcDebug>
  );
};

const RtcProviderWithExtraContexts = props => {
  return (
    <RtcStateProvider>
      <RTCProviderInjectDependencies
        {...props}
      />
    </RtcStateProvider>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RtcProviderWithExtraContexts);
